import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Save, KeyRound, Trash2, ExternalLink, Download } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { brl, numStr, parseNum } from "@/lib/format";
import { Button, Card, ErrorState, Field, Input, Loading, Page, PageHeader, StickyFooter } from "@/components/kit";
import { BrandFields, PlanSelect, StatusField } from "@/components/master/TenantFields";
import { StateBadge } from "@/pages/master/MasterHome";
import { DemoPanel } from "@/components/master/DemoTools";
import { PAY_LABEL } from "@/pages/Billing";

const toDate = (iso) => (iso ? iso.slice(0, 10) : "");
const toIso = (d) => (d ? `${d}T23:59:59+00:00` : null);

function fromServer(d) {
  return {
    f: { name: d.name, status: d.status, plan_id: d.plan_id, domains: (d.domains || []).join(", "), paid_until: toDate(d.paid_until), trial_until: toDate(d.trial_until),
      default_cmv_target: numStr(d.default_cmv_target || 30), contact_email: d.contact_email || "", contact_phone: d.contact_phone || "", notes: d.notes || "" },
    brand: { app_name: d.layout?.app_name, tagline: d.layout?.tagline, logo_url: d.layout?.logo_url, brand: d.layout?.brand, accent: d.layout?.accent },
  };
}

function Usage({ d }) {
  const u = d.usage;
  const items = [["Usuários", u.users], ["Unidades", u.units], ["Produtos", u.products], ["Conferências", u.conferences], ["Fichas", u.recipes]];
  return (
    <Card className="flex flex-col gap-3" data-testid="tenant-usage">
      <div className="flex items-center gap-2"><div className="h3 flex-1">Uso da plataforma</div><StateBadge state={d.state} data-testid="tenant-detail-state" /></div>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">{items.map(([l, v]) => <div key={l} className="rounded-xl p-3 min-w-0" style={{ background: "var(--surface-alt)" }}><div className="caption">{l}</div><div className="h3 tnum">{v}</div></div>)}</div>
      <div className="caption">Último acesso: {u.last_login_at ? new Date(u.last_login_at).toLocaleString("pt-BR") : "nunca"} · <a href={d.access_url} target="_blank" rel="noreferrer" className="underline inline-flex items-center gap-1" data-testid="tenant-access-link">{d.access_url} <ExternalLink size={11} /></a></div>
    </Card>
  );
}

function Admins({ d }) {
  const reset = useMutation({ mutationFn: (uid) => api(`/master/tenants/${d.id}/reset-admin`, { method: "POST", body: { user_id: uid } }), onError: (e) => toast.error(e.message) });
  return (
    <Card className="flex flex-col gap-2" data-testid="tenant-admins">
      <div className="h3">Administradores</div>
      {d.admins.map((a) => (
        <div key={a.id} className="flex flex-wrap items-center gap-3 py-1.5">
          <div className="flex-1 min-w-0"><div className="label">{a.name}</div><div className="caption">{a.email}</div></div>
          <Button size="sm" variant="ghost" icon={KeyRound} loading={reset.isPending && reset.variables === a.id} onClick={() => window.confirm(`Gerar nova senha para ${a.email}?`) && reset.mutate(a.id)} data-testid={`reset-admin-${a.id}`}>Nova senha</Button>
        </div>
      ))}
      {reset.data && <div className="rounded-xl p-3 text-sm" style={{ background: "var(--brand-tint)" }} data-testid="reset-admin-result">Nova senha: <b className="tnum">{reset.data.password}</b></div>}
    </Card>
  );
}

function Payments({ list }) {
  if (!list.length) return null;
  return (
    <Card className="flex flex-col gap-2" data-testid="tenant-payments">
      <div className="h3">Pagamentos</div>
      {list.map((p) => <div key={p.session_id} className="flex justify-between text-sm py-1"><span className="caption">{new Date(p.created_at).toLocaleString("pt-BR")}</span><span className="tnum">{brl(p.amount)} · {PAY_LABEL[p.payment_status] || p.payment_status}</span></div>)}
    </Card>
  );
}

function Danger({ d }) {
  const nav = useNavigate();
  const qc = useQueryClient();
  const { data: ov } = useQuery({ queryKey: ["master-overview"], queryFn: () => api("/master/overview") });
  const adopt = useMutation({ mutationFn: () => api(`/master/tenants/${d.id}/adopt-legacy`, { method: "POST" }), onSuccess: (r) => { qc.invalidateQueries(); toast.success(`${r.units} unidade(s) e ${r.users} usuário(s) vinculados`); }, onError: (e) => toast.error(e.message) });
  const del = useMutation({ mutationFn: () => api(`/master/tenants/${d.id}`, { method: "DELETE" }), onSuccess: () => { qc.invalidateQueries({ queryKey: ["master-overview"] }); nav("/master", { replace: true }); toast.success("Cliente excluído"); }, onError: (e) => toast.error(e.message) });
  const legacy = ov?.legacy || {};
  return (
    <Card className="flex flex-col gap-3">
      <div className="h3">Ações</div>
      {(legacy.units > 0 || legacy.users > 0) && (
        <Button variant="secondary" icon={Download} loading={adopt.isPending} className="self-start" data-testid="adopt-legacy-button"
          onClick={() => window.confirm(`Vincular os dados antigos (${legacy.units} unidade(s), ${legacy.users} usuário(s)) a ${d.name}? Isso não pode ser desfeito.`) && adopt.mutate()}>Importar dados anteriores à plataforma</Button>
      )}
      <Button variant="ghost" icon={Trash2} className="self-start" loading={del.isPending} onClick={() => window.confirm(`Excluir ${d.name} e todos os seus dados?`) && del.mutate()} data-testid="delete-tenant-button">Excluir cliente</Button>
    </Card>
  );
}

export default function TenantDetail() {
  const { id } = useParams();
  const qc = useQueryClient();
  const { data, isLoading, isError, error } = useQuery({ queryKey: ["master-tenant", id], queryFn: () => api(`/master/tenants/${id}`) });
  const [f, setF] = useState(null);
  const [brand, setBrand] = useState({});
  useEffect(() => { if (data) { const s = fromServer(data); setF(s.f); setBrand(s.brand); } }, [data]);
  const save = useMutation({
    mutationFn: () => api(`/master/tenants/${id}`, { method: "PUT", body: { ...f, paid_until: toIso(f.paid_until), trial_until: toIso(f.trial_until), default_cmv_target: parseNum(f.default_cmv_target) || 30, domains: f.domains.split(/[,\s]+/).filter(Boolean), brand } }),
    onSuccess: (d) => { qc.setQueryData(["master-tenant", id], d); qc.invalidateQueries({ queryKey: ["master-overview"] }); toast.success("Cliente atualizado"); },
    onError: (e) => toast.error(e.message),
  });
  if (isLoading || (data && !f)) return <Loading />;
  if (isError) return <Page><ErrorState message={error.message} /></Page>;
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  return (
    <Page>
      <PageHeader title={data.name} subtitle={data.slug} back="/master" />
      <div className="grid lg:grid-cols-[1fr_380px] gap-5 items-start">
        <div className="flex flex-col gap-5">
          <Card className="grid sm:grid-cols-2 gap-4">
            <Field label="Nome"><Input value={f.name} onChange={set("name")} data-testid="tenant-name-input" /></Field>
            <Field label="Domínios próprios" hint="Separe por vírgula"><Input value={f.domains} onChange={set("domains")} data-testid="tenant-domains-input" /></Field>
            <Field label="Status"><StatusField value={f.status} onChange={(v) => setF({ ...f, status: v })} withBlocked /></Field>
            <Field label="Plano"><PlanSelect value={f.plan_id} onChange={(v) => setF({ ...f, plan_id: v })} /></Field>
            <Field label="Pago até" hint="Atualizado automaticamente a cada pagamento"><Input type="date" value={f.paid_until} onChange={set("paid_until")} data-testid="tenant-paid-until-input" /></Field>
            <Field label="Teste até"><Input type="date" value={f.trial_until} onChange={set("trial_until")} data-testid="tenant-trial-until-input" /></Field>
            <Field label="Meta de CMV padrão (%)"><Input inputMode="decimal" value={f.default_cmv_target} onChange={set("default_cmv_target")} data-testid="tenant-cmv-input" /></Field>
            <Field label="E-mail de contato"><Input value={f.contact_email} onChange={set("contact_email")} data-testid="tenant-contact-email-input" /></Field>
            <div className="sm:col-span-2"><Field label="Observações internas"><textarea className="input" rows={2} value={f.notes} onChange={set("notes")} data-testid="tenant-notes-input" /></Field></div>
          </Card>
          <BrandFields brand={brand} setBrand={setBrand} />
        </div>
        <div className="flex flex-col gap-5"><Usage d={data} /><DemoPanel d={data} /><Admins d={data} /><Payments list={data.payments} /><Danger d={data} /></div>
      </div>
      <StickyFooter><Button icon={Save} loading={save.isPending} onClick={() => save.mutate()} data-testid="save-tenant-button">Salvar cliente</Button></StickyFooter>
    </Page>
  );
}
